import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { message } from 'antd';
import aiReplyService, {
  AIReplyBatchResponse,
  AIReplyGenerateResponse,
} from '../services/aiReplyService';
import { CommentTarget, GenerateParams } from '../components/common/reply/types';
import { AiMessage, ChatMessage } from '../components/common/reply/ConversationView';
import { GeneratedReply, PostWithReply } from '../services/replyPostsService';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface HistoryEntry {
  id: string;
  text: string;
  label: string;
  instruction?: string;
  variantStyle?: string;
  isAdvanced: boolean;
  timestamp: number;
}

export type ReplySessionStatus = 'composing' | 'generating' | 'ready' | 'error';

export interface ReplySession {
  id: string;
  post: PostWithReply;
  commentTarget: CommentTarget | null;
  initialTab: 'quick' | 'advanced';
  activeTab: 'quick' | 'advanced';
  status: ReplySessionStatus;
  currentStage: number;
  mode: 'compose' | 'conversation';
  messages: ChatMessage[];
  generateParams: GenerateParams | null;
  savedReplyTexts: string[];
  history: HistoryEntry[];
  refining: boolean;
  hasUnseenResult: boolean;
  createdAt: number;
}

export type OnReplySavedFn = (
  replyText: string,
  metadata: { brand_info?: string; tone_preference?: string; parent_comment_id?: string },
  options?: { silent?: boolean }
) => Promise<PostWithReply | void>;

/**
 * Invoked when the user explicitly asks to navigate to the source post from
 * inside the composer (via the "Go to Post" button). The callback receives the
 * latest known PostWithReply for the session — this is the post with the
 * just-generated reply attached when invoked after a successful save.
 */
export type OnGoToPostFn = (post: PostWithReply) => void;

export interface OpenSessionOpts {
  initialTab?: 'quick' | 'advanced';
  commentTarget?: CommentTarget | null;
  onReplySaved?: OnReplySavedFn;
  onGoToPost?: OnGoToPostFn;
}

// ---------------------------------------------------------------------------
// Context interface
// ---------------------------------------------------------------------------

interface ReplyComposerContextValue {
  sessions: ReplySession[];
  activeId: string | null;
  activeSession: ReplySession | null;
  openSession: (post: PostWithReply, opts?: OpenSessionOpts) => string;
  /**
   * Re-open the composer for a reply that's already been generated and saved.
   * If a session for this post already exists (e.g. minimized in the rail), it
   * is restored as-is. Otherwise a fresh session is seeded in `conversation`
   * mode with the saved reply pre-loaded as the latest AI message, so the user
   * can refine it via chat — exactly the same UX as right after generation.
   */
  openSessionForExistingReply: (
    post: PostWithReply,
    reply: GeneratedReply,
    opts?: OpenSessionOpts
  ) => string;
  restoreSession: (id: string) => void;
  minimizeActive: () => void;
  closeSession: (id: string) => void;
  updateSession: (id: string, patch: Partial<ReplySession>) => void;
  startGeneration: (id: string, kind: 'quick' | 'advanced', params: GenerateParams) => void;
  callOnReplySaved: (
    id: string,
    replyText: string,
    metadata: { brand_info?: string; tone_preference?: string; parent_comment_id?: string },
    options?: { silent?: boolean }
  ) => Promise<void>;
  /**
   * Triggers the parent-registered onGoToPost callback for the given session,
   * passing the session's latest known post (with reply attached after save).
   */
  callOnGoToPost: (id: string) => void;
}

const ReplyComposerContext = createContext<ReplyComposerContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const ReplyComposerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<ReplySession[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Refs for stable reads inside async callbacks
  const activeIdRef = useRef(activeId);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  // Per-session reply-saved callbacks stored outside of React state to avoid
  // stale closures and serialization issues
  const savedCallbacksRef = useRef<Map<string, OnReplySavedFn>>(new Map());

  // Per-session "go to post" callbacks. Kept in a ref for the same reason as
  // savedCallbacksRef — these closures hold references to parent-page state
  // setters and we don't want them re-serialized through React state.
  const goToPostCallbacksRef = useRef<Map<string, OnGoToPostFn>>(new Map());

  const activeSession = sessions.find((s) => s.id === activeId) ?? null;

  const updateSession = useCallback((id: string, patch: Partial<ReplySession>) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const openSession = useCallback(
    (post: PostWithReply, opts: OpenSessionOpts = {}): string => {
      const id = `rcs-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const tab = opts.initialTab ?? 'quick';
      const newSession: ReplySession = {
        id,
        post,
        commentTarget: opts.commentTarget ?? null,
        initialTab: tab,
        activeTab: tab,
        status: 'composing',
        currentStage: 0,
        mode: 'compose',
        messages: [],
        generateParams: null,
        savedReplyTexts: [],
        history: [],
        refining: false,
        hasUnseenResult: false,
        createdAt: Date.now(),
      };

      if (opts.onReplySaved) {
        savedCallbacksRef.current.set(id, opts.onReplySaved);
      }
      if (opts.onGoToPost) {
        goToPostCallbacksRef.current.set(id, opts.onGoToPost);
      }

      setSessions((prev) => [...prev, newSession]);
      setActiveId(id);
      return id;
    },
    []
  );

  const restoreSession = useCallback((id: string) => {
    setActiveId(id);
    setSessions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, hasUnseenResult: false } : s))
    );
  }, []);

  const openSessionForExistingReply = useCallback(
    (post: PostWithReply, reply: GeneratedReply, opts: OpenSessionOpts = {}): string => {
      const existing = sessions.find((s) => s.post.id === post.id);
      if (existing) {
        if (opts.onReplySaved) {
          savedCallbacksRef.current.set(existing.id, opts.onReplySaved);
        }
        if (opts.onGoToPost) {
          goToPostCallbacksRef.current.set(existing.id, opts.onGoToPost);
        }
        setActiveId(existing.id);
        setSessions((prev) =>
          prev.map((s) => (s.id === existing.id ? { ...s, hasUnseenResult: false } : s))
        );
        return existing.id;
      }

      const id = `rcs-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const tab: 'quick' | 'advanced' = opts.initialTab ?? 'quick';

      const syntheticReply: AIReplyGenerateResponse = {
        success: true,
        stage1: '',
        stage2: '',
        stage3: '',
        final_reply: reply.reply_text,
        metadata: {
          post_title: post.post_title,
          brand_info: reply.brand_info,
          tone_preference: reply.tone_preference ?? 'neutral',
        },
        warnings: [],
      };

      const aiMsg: AiMessage = {
        role: 'ai',
        reply: syntheticReply,
        isAdvanced: false,
      };

      const initialHistory: HistoryEntry = {
        id: `${Date.now()}-0`,
        text: reply.reply_text,
        label: 'Saved',
        isAdvanced: false,
        timestamp: Date.now(),
      };

      const generateParams: GenerateParams = {
        source: tab,
        post_title: post.post_title,
        post_content: post.post_selftext ?? '',
        post_url: post.post_permalink,
        subreddit: post.subreddit,
        brand_info: reply.brand_info,
        tone_preference: reply.tone_preference ?? 'neutral',
        capturedCommentId: reply.parent_comment_id,
      };

      const newSession: ReplySession = {
        id,
        post,
        commentTarget: opts.commentTarget ?? null,
        initialTab: tab,
        activeTab: tab,
        status: 'ready',
        currentStage: 4,
        mode: 'conversation',
        messages: [aiMsg],
        generateParams,
        savedReplyTexts: [reply.reply_text],
        history: [initialHistory],
        refining: false,
        hasUnseenResult: false,
        createdAt: Date.now(),
      };

      if (opts.onReplySaved) {
        savedCallbacksRef.current.set(id, opts.onReplySaved);
      }
      if (opts.onGoToPost) {
        goToPostCallbacksRef.current.set(id, opts.onGoToPost);
      }

      setSessions((prev) => [...prev, newSession]);
      setActiveId(id);
      return id;
    },
    [sessions]
  );

  const minimizeActive = useCallback(() => {
    setActiveId(null);
  }, []);

  const closeSession = useCallback(
    (id: string) => {
      const session = sessions.find((s) => s.id === id);
      if (!session) return;
      if (session.status === 'generating') {
        // Can't close while generating — minimize instead
        setActiveId(null);
        return;
      }
      savedCallbacksRef.current.delete(id);
      goToPostCallbacksRef.current.delete(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setActiveId((prev) => (prev === id ? null : prev));
    },
    [sessions]
  );

  const callOnReplySaved = useCallback(
    async (
      id: string,
      replyText: string,
      metadata: { brand_info?: string; tone_preference?: string; parent_comment_id?: string },
      options?: { silent?: boolean }
    ) => {
      const cb = savedCallbacksRef.current.get(id);
      if (cb) {
        try {
          // Parent saves the reply and may return the updated PostWithReply.
          // We use it to refresh the session's stored post so the drawer
          // header, rail tooltip, and "Go to Post" navigation all reflect
          // the latest server-side state (status, generated_reply, etc.).
          const updatedPost = await cb(replyText, metadata, options);
          if (updatedPost) {
            updateSession(id, { post: updatedPost });
          }
        } catch (err: any) {
          console.error('[ReplyComposerProvider] callOnReplySaved failed:', err);
          message.error(err?.message || 'Failed to save reply');
        }
      }
    },
    [updateSession]
  );

  const callOnGoToPost = useCallback(
    (id: string) => {
      const session = sessions.find((s) => s.id === id);
      if (!session) return;
      const cb = goToPostCallbacksRef.current.get(id);
      if (cb) {
        try {
          cb(session.post);
        } catch (err: any) {
          console.error('[ReplyComposerProvider] callOnGoToPost failed:', err);
        }
      }
    },
    [sessions]
  );

  const startGeneration = useCallback(
    (id: string, kind: 'quick' | 'advanced', params: GenerateParams) => {
      updateSession(id, {
        status: 'generating',
        currentStage: 0,
        generateParams: params,
      });

      const { source, capturedCommentId, ...apiRequest } = params as any;

      // Stage animation runs in parallel with the API call
      const stagePromise = (async () => {
        for (let i = 0; i < 4; i++) {
          updateSession(id, { currentStage: i });
          await new Promise<void>((r) => setTimeout(r, 5000));
        }
      })();

      const apiPromise =
        kind === 'quick'
          ? aiReplyService.generateReply(apiRequest)
          : aiReplyService.generateAdvancedVariants(apiRequest);

      Promise.all([apiPromise, stagePromise])
        .then(([apiResult]) => {
          let messages: ChatMessage[];
          let firstReplyText: string;
          let firstVariantStyle: string | undefined;

          if (kind === 'quick') {
            const result = apiResult as AIReplyGenerateResponse;
            const aiMsg: AiMessage = { role: 'ai', reply: result, isAdvanced: false };
            messages = [aiMsg];
            firstReplyText = result.final_reply;
          } else {
            const { variations } = apiResult as AIReplyBatchResponse;
            if (!variations || variations.length === 0) {
              throw new Error('No variations returned by the server');
            }
            const aiMsg: AiMessage = {
              role: 'ai',
              reply: variations[0],
              variations,
              isAdvanced: true,
            };
            messages = [aiMsg];
            firstReplyText = variations[0].final_reply;
            firstVariantStyle = variations[0].metadata?.variant_style ?? undefined;
          }

          const firstHistoryEntry: HistoryEntry = {
            id: `${Date.now()}-0`,
            text: firstReplyText,
            label: 'Original',
            variantStyle: firstVariantStyle,
            isAdvanced: kind === 'advanced',
            timestamp: Date.now(),
          };

          const isMinimized = activeIdRef.current !== id;

          updateSession(id, {
            status: 'ready',
            currentStage: 4,
            mode: 'conversation',
            messages,
            history: [firstHistoryEntry],
            savedReplyTexts: [firstReplyText],
            hasUnseenResult: isMinimized,
          });

          // Auto-save via InboxPage callback. Routed through callOnReplySaved
          // so the returned PostWithReply (with the just-generated reply
          // attached) is patched onto session.post — this is what makes the
          // "Go to Post" button land on the post in its updated state.
          void callOnReplySaved(
            id,
            firstReplyText,
            {
              brand_info: params.brand_info,
              tone_preference: params.tone_preference,
              parent_comment_id: capturedCommentId,
            },
            { silent: false }
          );

          if (isMinimized) {
            const postTitle = sessions.find((s) => s.id === id)?.post.post_title;
            const sub = sessions.find((s) => s.id === id)?.post.subreddit;
            const label = postTitle
              ? postTitle.slice(0, 40) + (postTitle.length > 40 ? '…' : '')
              : `r/${sub}`;
            message.success(`Reply ready — ${label}`);
          }
        })
        .catch((err: any) => {
          updateSession(id, { status: 'error', currentStage: 0 });
          message.error(err?.message || 'Failed to generate reply');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateSession, sessions, callOnReplySaved]
  );

  return (
    <ReplyComposerContext.Provider
      value={{
        sessions,
        activeId,
        activeSession,
        openSession,
        openSessionForExistingReply,
        restoreSession,
        minimizeActive,
        closeSession,
        updateSession,
        startGeneration,
        callOnReplySaved,
        callOnGoToPost,
      }}
    >
      {children}
    </ReplyComposerContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useReplyComposer = (): ReplyComposerContextValue => {
  const ctx = useContext(ReplyComposerContext);
  if (!ctx) {
    throw new Error('useReplyComposer must be used inside ReplyComposerProvider');
  }
  return ctx;
};
